import { useState } from "react";
import { Calculator, Loader2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";

export const SQ = 10.7639;
const RATE_KEYS = ["frame_rate", "sash_std_rate", "sash_over_rate", "crate_rate"];
const AREA_KEYS = ["min_area", "crate_min_area", "sash_min_area", "min_area_double", "valid_min", "valid_max"];
const n = (v) => (v === "" || v == null ? v : Math.round(Number(v) * 1e6) / 1e6);

// $/sq ft ⇄ $/m² view: rates × 10.7639, areas ÷ 10.7639 (k = 1 to m², -1 back to sq ft). Screens' frame_rate is a $/area rate too.
export function convert(f, k) {
  const r = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? v : n(k > 0 ? Number(v) * SQ : Number(v) / SQ));
  const a = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? v : n(k > 0 ? Number(v) / SQ : Number(v) * SQ));
  return {
    ...f, ...Object.fromEntries(RATE_KEYS.map((x) => [x, r(f[x])])), ...Object.fromEntries(AREA_KEYS.map((x) => [x, a(f[x])])),
    glass_tiers: (f.glass_tiers || []).map((t) => ({ ...t, from_sqft: a(t.from_sqft), to_sqft: a(t.to_sqft), rate: r(t.rate) })),
    glass_upgrades: (f.glass_upgrades || []).map((u) => ({ ...u, rate: r(u.rate) })),
    tracks: (f.tracks || []).map((t) => ({ ...t, rate: r(t.rate) })),
    extras: (f.extras || []).map((e) => (e.unit === "sqft" ? { ...e, rate: r(e.rate) } : e)),
  };
}

// Inline validation before saving: no negatives, bands from < to and not overlapping, screen range min ≤ max.
export function problems(f) {
  const out = [];
  const nums = [...RATE_KEYS, ...AREA_KEYS, "sash_max_w", "sash_max_h", "base_price", "min_price"].filter((k) => f[k] !== "" && f[k] != null);
  nums.forEach((k) => { if (Number.isNaN(Number(f[k])) || Number(f[k]) < 0) out.push(`${k.replace(/_/g, " ")} must be a number of 0 or more`); });
  [...(f.glass_tiers || []), ...(f.glass_upgrades || []), ...(f.extras || []), ...(f.tracks || [])].forEach((r) => {
    if (Number(r.rate) < 0 || Number.isNaN(Number(r.rate))) out.push(`${r.name || "A row"}: rate must be 0 or more`);
  });
  const t = [...(f.glass_tiers || [])].map((x) => [Number(x.from_sqft), Number(x.to_sqft)]).sort((x, y) => x[0] - y[0]);
  t.forEach(([a, b]) => { if (!(b > a)) out.push(`Glass band ${a}–${b}: "up to" must be above "over"`); });
  t.slice(1).forEach(([a], i) => { if (a < t[i][1] - 1e-9) out.push(`Glass bands overlap (${t[i][0]}–${t[i][1]} and ${a}–${t[i + 1][1]})`); });
  if (Number(f.valid_max) && Number(f.valid_min) > Number(f.valid_max)) out.push("Valid range: from is above to");
  if (Number(f.sash_over_rate) && Number(f.sash_std_rate) > Number(f.sash_over_rate)) out.push("Standard sash rate is above the oversized rate");
  return out;
}

export function TestPriceBox({ body }) {
  const [t, setT] = useState({ w: "48", h: "60", layout: "", sash: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const sash_sizes = t.sash.split(/[,;]+/).map((p) => p.toLowerCase().split(/[x×*]/).map(Number)).filter((s) => s.length === 2 && s[0] > 0 && s[1] > 0);
      const b = body();
      const { data } = await api.post("/settings/product-lines/test-price", {
        line: b, width_in: Number(t.w), height_in: Number(t.h), sash_sizes, layout: b.kind === "slider" ? "" : t.layout,
        config: b.kind === "slider" ? (t.layout || b.default_layout || "XO").replace(/\|/g, "") : "", track: b.tracks?.[0]?.name || "",
      });
      setRes(data);
    } catch (e) {
      setRes({ error: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const cell = `${inputCls} text-right font-mono-num`;
  return (
    <div data-testid="line-test-price" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-200"><Calculator className="h-3.5 w-3.5" /> Test price (unsaved edits)</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <label className="text-xs text-slate-400">W (in)<input data-testid="test-price-w" value={t.w} onChange={(e) => setT({ ...t, w: e.target.value })} className={cell} /></label>
        <label className="text-xs text-slate-400">H (in)<input data-testid="test-price-h" value={t.h} onChange={(e) => setT({ ...t, h: e.target.value })} className={cell} /></label>
        <label className="text-xs text-slate-400">Layout<input data-testid="test-price-layout" value={t.layout} placeholder="default" onChange={(e) => setT({ ...t, layout: e.target.value })} className={`${inputCls} font-mono`} /></label>
        <label className="text-xs text-slate-400">Sash W×H<input data-testid="test-price-sash" value={t.sash} placeholder="equal" onChange={(e) => setT({ ...t, sash: e.target.value })} className={`${inputCls} font-mono`} /></label>
        <button data-testid="test-price-run" onClick={run} disabled={busy} className="mt-4 inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-amber-500 px-3 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Price it</button>
      </div>
      {res?.error && <p data-testid="test-price-error" className="mt-2 text-xs text-red-400">{res.error}</p>}
      {res && !res.error && (
        <div data-testid="test-price-result" className="mt-2 text-xs text-slate-300">
          {res.breakdown.map((p, i) => <div key={i} className="flex justify-between gap-3"><span>{p.label}</span><span className="font-mono-num">${p.amount.toFixed(2)}</span></div>)}
          <div className="mt-1 flex justify-between border-t border-slate-800 pt-1 font-semibold text-slate-100">
            <span>Cost{res.warning ? ` · ${res.warning}` : ""}</span><span data-testid="test-price-cost" className="font-mono-num">${res.cost.toFixed(2)} → sell ${res.suggested.toFixed(2)}</span></div>
        </div>
      )}
    </div>
  );
}
